/**
 * useImportExistingMatches
 *
 * Consulta no próprio banco o que já existe no tenant antes de importar:
 *   - cliente com o mesmo CPF/CNPJ;
 *   - veículos com as placas/chassis retornados pelo provider (com os valores
 *     atuais, para comparação campo a campo).
 *
 * Serve para (a) sinalizar duplicidade no card do cliente e (b) evitar consultas
 * FIPE/placa desnecessárias em veículos já cadastrados (consumo de créditos).
 *
 * RELEASE: 2026-08-23 v1.0.0 - Versão inicial.
 */

import { useMutation } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { ExistingVehicleSnapshot } from '@/lib/vehicle-import-payload';

/** Cliente já cadastrado no tenant */
export interface ExistingCustomerMatch {
  customer_id: string;
  name: string | null;
  customer_type: string | null;
  main_contact_id: string | null;
  created_at: string | null;
}

/** Veículo já cadastrado no tenant */
export interface ExistingVehicleMatch {
  vehicle_id: string;
  plate: string | null;
  chassis: string | null;
  customer_id: string | null;
  contact_id: string | null;
  belongs_to_customer_name: string | null;
  current: ExistingVehicleSnapshot;
}

export interface ImportExistingMatches {
  customer: ExistingCustomerMatch | null;
  vehicles: ExistingVehicleMatch[];
}

/** Normaliza placa/chassi da mesma forma que o banco (comparação estável) */
export function normalizeIdentifier(value: string | null | undefined): string {
  return (value ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

export function useImportExistingMatches() {
  return useMutation({
    mutationFn: async (input: {
      document: string;
      plates: (string | null)[];
      chassis: (string | null)[];
      tenantId?: string | null;
    }): Promise<ImportExistingMatches> => {
      const { data, error } = await (supabase.rpc as any)('get_import_existing_matches', {
        p_document: (input.document ?? '').replace(/\D+/g, ''),
        p_plates: input.plates.map(normalizeIdentifier).filter(Boolean),
        p_chassis: input.chassis.map(normalizeIdentifier).filter(Boolean),
        p_tenant_hint: input.tenantId ?? null,
      });

      if (error) {
        logger.error('ImportExistingMatches', 'Falha ao consultar duplicidade', {
          message: error.message,
        });
        throw new Error('Não foi possível verificar se o cliente já está cadastrado');
      }

      return {
        customer: (data?.customer ?? null) as ExistingCustomerMatch | null,
        vehicles: ((data?.vehicles ?? []) as ExistingVehicleMatch[]),
      };
    },
  });
}
